import { IgnoredErrorInfo } from '../components/spreadsheet/SpreadsheetTypes';
import { IResponseData } from '../types/spreadsheet-types';
import {
  ColorMap,
  IValidation,
  IValidationForExtraction,
  IValidationErrorByExtraction,
  IValidationErrorByExtractionDetails,
  TValidationType,
  ValidationDismissalReason,
} from '../types/validation-types';
import { LABEL_LIMIT } from '../constants';

export interface ValidationResult {
  isValid: boolean;
  errorMessage: string;
}

// The grid component returns color values in either "rgb" or "rgba" formats.
// Therefore, when testing with Cypress, we should consistently use the "rgb" format for color assertions.
export const ignoredFieldIconColor = 'rgb(255, 193, 7, 0.67)';

export const colorMap: ColorMap = {
  standard: {
    invalid: {
      backgroundColor: 'rgba(220, 53, 69, 0.77)',
      color: 'rgb(255, 0, 0)',
      gridColor: 'rgb(0, 0 ,0)',
    },
    missing: {
      backgroundColor: 'rgba(255, 193, 7, 0.67)',
      color: 'rgb(255, 165, 0)',
      gridColor: 'rgb(0, 0 ,0)',
    },
    columnSelected: {
      backgroundColor: 'rgb(29, 154, 170)',
      color: 'rgb(255, 255, 255)',
      gridColor: 'rgb(255, 255, 255)',
    },
    inactive: {
      backgroundColor: 'rgb(211, 211, 211)',
      color: 'rgb(211, 211, 211)',
    },
    transparent: {
      backgroundColor: 'rgba(0, 0, 0, 0)',
    },
  },
  accessibility: {
    invalid: {
      backgroundColor: 'rgb(178, 34, 34)',
      color: 'rgb(178, 34, 34)',
      gridColor: 'rgb(255, 255, 255)',
    },
    missing: {
      backgroundColor: 'rgba(255, 140, 0, 1)',
      color: 'rgba(255, 140, 0, 1)',
      gridColor: 'rgb(255, 255, 255)',
    },
    columnSelected: {
      backgroundColor: 'rgb(29, 154, 170)',
      color: 'rgb(255, 255, 255)',
      gridColor: 'rgb(255, 255, 255)',
    },
    inactive: {
      backgroundColor: 'rgb(211, 211, 211)',
      color: 'rgb(211, 211, 211)',
    },
    transparent: {
      backgroundColor: 'rgba(0, 0, 0, 0)',
    },
  },
};

export const isRequiredAndValueEmpty = (
  validationType: TValidationType | undefined,
  value: string
) => validationType === 'required' && value === '';

export const whichColorMapObject = (accessibility: boolean) => {
  return accessibility ? 'accessibility' : 'standard';
};

export const validationTooltipMessage = (
  isStatusInvalidAndMissing: boolean,
  error?: string | null,
  errorDetail?: string | null
) => {
  if (error) {
    return errorDetail ? `${error}: ${errorDetail}` : `${error}`;
  } else {
    return `${isStatusInvalidAndMissing ? `Value Missing` : `${error}`}`;
  }
};

// Generic interface for validation objects (works with both review and mark)
interface IValidationLike {
  error_detail?: string | null;
  label?: string;
  error?: string | null;
}

// Determine the validation error type from a validation object
// This is a generic function that works with both IValidation (review) and IMarkValidation (mark)
export const getValidationType = (validation: IValidationLike | undefined): TValidationType => {
  if (!validation) {
    return 'unknown';
  }

  if (validation.error_detail === 'date') {
    return 'date';
  } else if (validation.error_detail === 'number') {
    return 'number';
  } else if (validation.error_detail === 'amount') {
    return 'amount';
  } else if (validation.error_detail === validation.label) {
    return 'required';
  } else if (validation.error?.toLowerCase() === 'failed cross check') {
    return 'currentcharges';
  } else {
    return 'unknown';
  }
};

// Alias for backward compatibility with review code
export const extractionValidationType = (
  extractionValidation: IValidation | undefined
): TValidationType => {
  return getValidationType(extractionValidation);
};

// loop through all validation errors and compile an array of strings that have the validation error types
// FIXME: We can maybe remove this later?
export const collectValidationErrorTypes = (
  extractionInValidationResponse: IValidationForExtraction
) => {
  return extractionInValidationResponse.results.map((validation: any) => {
    return extractionValidationType(validation);
  });
};

// loop through all validation errors and compile a list of fields and errors with the error types
export const collectValidationErrorDetails = (
  extractionInValidationResponse: IValidationForExtraction
) => {
  return extractionInValidationResponse.results.map((validation: IValidation) => {
    return {
      id: validation.id,
      field: validation.label,
      error: validation.error,
      errorDetail: validation.error_detail,
      type: extractionValidationType(validation),
      dataset: validation.dataset || '',
      dismissalReason: validation.dismissal_reason,
    };
  });
};

// Build the validation summary array for a set of visible extraction ids
export const buildValidationSummaryByExtractionId = (
  visibleExtractionIds: string[],
  extractionDataVal: any
): IValidationErrorByExtraction[] => {
  return visibleExtractionIds.map((extractionId: string) => {
    const matchingExtraction: any = Array.isArray(extractionDataVal)
      ? extractionDataVal.find((extraction: any) => extraction?.data?.id === extractionId)
      : undefined;

    const validationData = matchingExtraction?.data;

    return {
      extractionId,
      hasValidationResponse: !!matchingExtraction,
      validationErrorsList: validationData ? collectValidationErrorTypes(validationData) : [],
      validationErrorDetails: validationData ? collectValidationErrorDetails(validationData) : [],
    };
  });
};

// Generic interface for validation error details (works with both review and mark)
export interface IValidationErrorDetailsGeneric {
  id: number;
  field: string;
  error: string | null;
  errorDetail: string | null;
  type: TValidationType;
  dataset?: string; // Only present in review
  dismissalReason?: ValidationDismissalReason | null;
}

// Generic interface for validation summary by entity (extraction or document)
export interface IValidationSummaryGeneric {
  entityId: string;
  hasValidationResponse: boolean;
  validationErrorsList: TValidationType[];
  validationErrorDetails: IValidationErrorDetailsGeneric[];
}

// Generic function to collect validation error details from a validation response
// Works with both review (IValidation) and mark (IMarkValidation) validation objects
export const collectValidationErrorDetailsGeneric = <T extends IValidationLike & { id: number; dismissal_reason?: ValidationDismissalReason | null; dataset?: string | null }>(
  results: T[],
  includeDataset: boolean = false
): IValidationErrorDetailsGeneric[] => {
  return results.map((validation) => ({
    id: validation.id,
    field: validation.label || '',
    error: validation.error || null,
    errorDetail: validation.error_detail || null,
    type: getValidationType(validation),
    ...(includeDataset ? { dataset: validation.dataset || '' } : {}),
    dismissalReason: validation.dismissal_reason,
  }));
};

// Generic function to build validation summary for a set of entity IDs
// Works with both extractions (review) and documents (mark)
export const buildValidationSummaryGeneric = (
  entityIds: string[],
  validationData: any[],
  includeDataset: boolean = false
): IValidationSummaryGeneric[] => {
  return entityIds.map((entityId: string) => {
    const matchingEntity = Array.isArray(validationData)
      ? validationData.find((item: any) => item?.data?.id === entityId)
      : undefined;

    const data = matchingEntity?.data;
    const results = data?.results || [];

    return {
      entityId,
      hasValidationResponse: !!matchingEntity && !!data,
      validationErrorsList: results.map((v: IValidationLike) => getValidationType(v)),
      validationErrorDetails: collectValidationErrorDetailsGeneric(results, includeDataset),
    };
  });
};

export const validateLabel = (label: string, existingLabels: string[]): ValidationResult => {
  if (label.length === 0) {
    return { isValid: false, errorMessage: 'Name cannot be empty!' };
  } else if (label.length > LABEL_LIMIT) {
    return { isValid: false, errorMessage: `Name must be less than ${LABEL_LIMIT} characters` };
  } else if (existingLabels.includes(label)) {
    return { isValid: false, errorMessage: 'Name already exists!' };
  } else if (label.includes('.')) {
    return { isValid: false, errorMessage: 'Periods cannot be in field names.' };
  }

  return { isValid: true, errorMessage: '' };
};

/**
 * Check whether any validation query results contain undismissed transform errors.
 * Used by ExtractionBatchHeader to decide whether to show the badge on the Transformed tab.
 */
export const hasUndismissedTransformErrors = (
  validationResults: IValidationForExtraction[]
): boolean => {
  return validationResults.some((result) =>
    result.results.some(
      (r) => r.dataset === 'transformed' && !r.dismissal_reason
    )
  );
};

/**
 * Check whether any extraction in the validation summary has undismissed errors.
 * Used by the validation badge and to detect when the batch-level
 * has_undismissed_validation_errors flag is out of sync with local validation data.
 */
export const hasUndismissedValidationErrors = (
  validationSummary: IValidationErrorByExtraction[]
): boolean => {
  return validationSummary.some((extraction) =>
    extraction.validationErrorDetails?.some((error) => !error.dismissalReason)
  );
};

export const findValidationErrorsByExtractionId = (
  extractionDataVal: any,
  extractionId: string
) => {
  let findValidationErrors: IValidationForExtraction | undefined = undefined;
  if (Array.isArray(extractionDataVal)) {
    findValidationErrors = extractionDataVal.find((e: any) => e.id === extractionId);
  }

  return findValidationErrors;
};

export const extractionFieldHasIgnoredErrors = (
  validationErrorDetails: IValidationErrorByExtractionDetails[],
  field: string
) => {
  // Check if any validation error for this field has a dismissalReason set
  const fieldError = validationErrorDetails.find((error) => error.field === field);
  return fieldError ? fieldError.dismissalReason !== null : false;
};

export const getCellStyles = (params: {
  accessibility: boolean;
  validationStatus: 'required' | 'error' | null;
  isIgnored: boolean;
  isHighlighted: boolean;
}) => {
  const { accessibility, validationStatus, isIgnored, isHighlighted } = params;
  const colorMode = whichColorMapObject(accessibility);

  // If the cell is highlighted, always use the highlight color
  if (isHighlighted) {
    return {
      backgroundColor: colorMap[colorMode].columnSelected.backgroundColor,
      color: colorMap[colorMode].columnSelected.gridColor,
    };
  }

  // If the cell is ignored but not highlighted, use white background
  if (isIgnored) {
    return {
      color: 'inherit',
    };
  }

  // For non-highlighted, non-ignored cells, use validation-based styling
  if (validationStatus === 'error') {
    return {
      backgroundColor: colorMap[colorMode].invalid.backgroundColor,
      color: colorMap[colorMode].invalid.gridColor,
    };
  }

  if (validationStatus === 'required') {
    return {
      backgroundColor: colorMap[colorMode].missing.backgroundColor,
      color: colorMap[colorMode].missing.gridColor,
    };
  }

  return {};
};

// Return an array of strings that includes all of the validation errors for a given field
// Duplicates are removed to avoid showing the same message multiple times
export const getValidationMessages = (
  validationErrorsForThisField: IValidationErrorByExtractionDetails[] | [],
  fieldContent: string
) => {
  const messages =
    validationErrorsForThisField
      ?.map((validation) =>
        validationTooltipMessage(
          isRequiredAndValueEmpty(validation.type, fieldContent),
          validation.error,
          validation.errorDetail
        )
      )
      .filter(Boolean) || [];

  // Remove duplicate messages
  return [...new Set(messages)];
};

// A field may have multiple validation errors (and a required/error error). We want to prioritize
// the `error` type so a field is always displayed red if it has an error and a required error (the
// actual error should be prioritized over the "missing"/required error)
export const getHighestPriorityValidationError = (
  validationErrorsForThisField: IValidationErrorByExtractionDetails[]
) => {
  const findError = (validationErrorsForThisField ?? []).find(
    (validation) => validation.type !== 'required'
  );

  return findError || (validationErrorsForThisField ?? [])[0];
};

// Get all validation errors for a specific field in the extraction
export const getValidationErrorsForSingleField = (
  validationErrorsForThisExtraction: IValidationErrorByExtraction | undefined,
  fieldsKey: string
): IValidationErrorByExtractionDetails[] => {
  return (
    validationErrorsForThisExtraction?.validationErrorDetails?.filter(
      (validation) => validation.field === fieldsKey
    ) || []
  );
};

/**
 * Merges validation summaries by dataset type. Incoming errors REPLACE all existing
 * errors of the same dataset type(s), while preserving other dataset types.
 *
 * Example: incoming 'extracted'+'schematized' errors replace existing ones,
 * but 'transformed' errors are preserved.
 */
export const mergeValidationSummaries = (
  existing: IValidationErrorByExtraction[],
  incoming: IValidationErrorByExtraction[],
  datasetTypesToClear?: string[]
): IValidationErrorByExtraction[] => {
  // Use provided dataset types to clear, or fall back to detecting from incoming errors
  const datasetsToReplace = datasetTypesToClear
    ? new Set(datasetTypesToClear)
    : new Set(
        incoming.flatMap((e) => e.validationErrorDetails.map((d) => d.dataset).filter(Boolean))
      );

  // Build map from existing, then merge incoming
  const map = new Map(existing.map((e) => [e.extractionId, e]));

  for (const inc of incoming) {
    const ext = map.get(inc.extractionId);
    // Filter out existing errors whose dataset is in datasetsToReplace, then add incoming errors
    const mergedDetails = [
      ...(ext?.validationErrorDetails.filter(
        (d) => !d.dataset || !datasetsToReplace.has(d.dataset)
      ) ?? []),
      ...inc.validationErrorDetails,
    ];

    map.set(inc.extractionId, {
      extractionId: inc.extractionId,
      hasValidationResponse: ext?.hasValidationResponse || inc.hasValidationResponse,
      validationErrorsList: [...new Set(mergedDetails.map((d) => d.type))],
      validationErrorDetails: mergedDetails,
    });
  }

  return Array.from(map.values());
};
